import { ReactNode, useState } from "react";
import { Box, Button, Theme, Typography, alpha } from "@mui/material";
import { yellow } from "@mui/material/colors";
import { IVacationAwardingSlotUsage, IVacationBidPreference, IVacationBidSlot } from "@vmosolutions/oc-interfaces";

import { VACATION_BID_TEST_ID } from "../testIds";
import { displaySlotId } from "../helpers";
import { FillBand, fillBand, slotDateRange, slotLengthDays } from "./awardingHelpers";

export const fillBandColor = (theme: Theme, band: FillBand): string => {
  if (band === "full") return theme.palette.error.main;
  if (band === "nearlyFull") return theme.palette.warning.light;
  if (band === "halfFull") return yellow[600];
  return theme.palette.success.main;
};

export interface ISlotAvailabilityProps {
  usage: IVacationAwardingSlotUsage | undefined;
}

/** "2 of 18 slots left", coloured by how full the slot is. */
export const SlotAvailability = (props: ISlotAvailabilityProps): JSX.Element => {
  const { usage } = props;
  if (!usage) {
    return (
      <Typography variant="caption" sx={(theme) => ({ color: theme.custom.forms.labels })}>
        No longer offered
      </Typography>
    );
  }
  const band = fillBand(usage);
  return (
    <Typography
      variant="caption"
      sx={(theme) => ({ color: band === "open" ? theme.custom.forms.labels : fillBandColor(theme, band) })}
    >
      {`${Math.max(0, usage.remaining)} of ${usage.capacity} slots left`}
    </Typography>
  );
};

export interface IRankedBidListProps {
  preferences: IVacationBidPreference[];
  slotsById: Map<string, IVacationBidSlot>;
  usageBySlot: Map<string, IVacationAwardingSlotUsage>;
  showSlotIdYear: boolean;
  /** Rows shown before "Show all". Every row is shown when absent. */
  initialCount?: number;
  /** Slots to mark as awarded. */
  awardedSlotIds?: Set<string>;
  /** Day counts to show in place of a slot's length, such as an awarded short block's. */
  daysLabels?: Map<string, string>;
  /** One line per row, content-sized columns, note last. Needs a container that scrolls sideways. */
  compact?: boolean;
  /** The right-hand end of a row. Defaults to the slot's availability. */
  renderStatus?: (
    preference: IVacationBidPreference,
    slot: IVacationBidSlot | undefined,
    usage: IVacationAwardingSlotUsage | undefined
  ) => ReactNode;
}

const RankedBidListStyles = (theme: Theme): any => ({
  display: "flex",
  flexDirection: "column",
  gap: "2px",
  "& .ranked_bid_row": {
    display: "grid",
    gridTemplateColumns: "28px 104px 120px auto minmax(0, 1fr) auto",
    alignItems: "center",
    columnGap: "12px",
    minHeight: "36px",
    padding: "2px 10px",
    borderRadius: "3px",
    border: "1px solid transparent",
    "&:not(:last-of-type)": { borderBottomColor: theme.custom.forms.inputBorder },
    "&.awarded": {
      backgroundColor: alpha(theme.palette.success.dark, 0.2),
      borderColor: theme.palette.success.main,
    },
    "& .rank": { color: theme.custom.forms.labels },
    "& .slot_id": { fontWeight: 700, whiteSpace: "nowrap" },
    "& .days": {
      justifySelf: "start",
      padding: "0 6px",
      fontSize: 11,
      border: `1px solid ${theme.custom.forms.inputBorder}`,
      borderRadius: "3px",
    },
    "& .note": {
      fontStyle: "italic",
      color: theme.custom.forms.labels,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    "& .status": {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-end",
      gap: "10px",
      "& .MuiButtonBase-root": { padding: "1px 10px", fontSize: 12, minWidth: 0 },
    },
  },
  "& .show_all": {
    alignSelf: "flex-start",
    fontSize: 12,
    textTransform: "none",
    padding: "2px 10px",
  },
});

const compactStyles = (showSlotIdYear: boolean): any => ({
  // As wide as the widest row, so every row lines up and a narrow pane scrolls sideways.
  minWidth: "max-content",
  "& .ranked_bid_row": {
    gridTemplateColumns: `20px ${showSlotIdYear ? "96px" : "60px"} 112px 56px minmax(120px, max-content) minmax(0, 1fr)`,
    columnGap: "10px",
    padding: "2px 8px",
    minHeight: "32px",
    "& > *": { whiteSpace: "nowrap" },
    "& .status": { order: 1, justifyContent: "flex-start" },
    "& .note": { order: 2, maxWidth: "240px" },
  },
});

/** A crew member's ranked blocks, one row each, most wanted first. */
export const RankedBidList = (props: IRankedBidListProps): JSX.Element => {
  const {
    preferences,
    slotsById,
    usageBySlot,
    showSlotIdYear,
    initialCount,
    awardedSlotIds,
    daysLabels,
    renderStatus,
    compact,
  } = props;
  const [showAll, setShowAll] = useState<boolean>(false);

  const ordered = [...preferences].sort((a, b) => a.rank - b.rank);
  const visible = initialCount === undefined || showAll ? ordered : ordered.slice(0, initialCount);

  return (
    <Box
      sx={[RankedBidListStyles, compact ? compactStyles(showSlotIdYear) : {}]}
      data-testid={VACATION_BID_TEST_ID.rankedBidList}
    >
      {visible.map((preference) => {
        const slot = slotsById.get(preference.slotId);
        const usage = usageBySlot.get(preference.slotId);
        return (
          <div
            key={preference.slotId}
            className={`ranked_bid_row${awardedSlotIds?.has(preference.slotId) ? " awarded" : ""}`}
            data-testid={VACATION_BID_TEST_ID.rankedBidRow}
          >
            <Typography variant="body2" className="rank">
              {preference.rank}
            </Typography>
            <Typography variant="body2" className="slot_id">
              {displaySlotId(preference.slotId, showSlotIdYear)}
            </Typography>
            <Typography variant="body2">{slot ? slotDateRange(slot) : ""}</Typography>
            <Typography variant="caption" className="days">
              {daysLabels?.get(preference.slotId) ?? (slot ? `${slotLengthDays(slot)} days` : "-")}
            </Typography>
            <Typography variant="body2" className="note" title={preference.note ?? ""}>
              {preference.note ? `"${preference.note}"` : ""}
            </Typography>
            <div className="status">
              {renderStatus ? renderStatus(preference, slot, usage) : <SlotAvailability usage={usage} />}
            </div>
          </div>
        );
      })}
      {initialCount !== undefined && ordered.length > initialCount && (
        <Button
          size="small"
          className="show_all"
          onClick={() => setShowAll((current) => !current)}
          data-testid={VACATION_BID_TEST_ID.showAllBidsButton}
        >
          {showAll ? "Show fewer bids" : `Show all ${ordered.length} bids`}
        </Button>
      )}
    </Box>
  );
};

export default RankedBidList;
